import { RolodexEntryJson } from 'types/rolodex/rolodexEntries'
import { parseArray, parseNumber, parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'

export const DERIVED_SPATIAL_DATASET_DEFAULT_FILES = {
  points: 'points.parquet',
  groups: 'groups.parquet',
  pointGroups: 'point_groups.parquet',
  tileset: 'render/3dtiles/tileset.json',
} as const

export interface DerivedSpatialDatasetFiles extends Record<string, unknown> {
  points_parquet: string
  groups_parquet: string
  point_groups_parquet: string
  tileset?: string
  tile_payloads?: string | string[]
}

export interface DerivedSpatialDatasetGeneration extends Record<
  string,
  unknown
> {
  source?: string
  created_by?: string
  processing_signature?: string
  group_generation_signature?: string
  tile_generation_signature?: string
}

export interface DerivedSpatialDatasetGeometry extends Record<string, unknown> {
  x_measurement_unit?: string
  y_measurement_unit?: string
  z_measurement_unit?: string
}

export interface GeckoDerivedSpatialDatasetData extends Record<
  string,
  unknown
> {
  dataset_kind: string
  schema_version?: number
  data_version?: number
  status?: string
  created_at?: string
  source_inspection_id?: string
  source_inspection_run_ids: string[]
  point_schema_version?: string
  group_schema_version?: string
  geometry?: DerivedSpatialDatasetGeometry
  amplitude_measurement_unit?: string
  depth_measurement_unit?: string
  files: DerivedSpatialDatasetFiles
  generation?: DerivedSpatialDatasetGeneration
  error_summary?: string | null
}

/**
 * Derived spatial datasets are thin Rolodex metadata entries that point to
 * canonical Parquet tables and optional 3D Tiles render artifacts.
 */
export class GeckoDerivedSpatialDataset extends GeckoEntryBase<
  GeckoDerivedSpatialDatasetData,
  {
    site: string
    unit: string
    component: string
    inspection: string
    organization: string
  },
  Record<string, never>
> {
  private constructor(json: RolodexEntryJson) {
    const files = (json.data.files ?? {}) as Record<string, unknown>
    const generation = (json.data.generation ?? {}) as Record<string, unknown>
    const geometry = (json.data.geometry ?? {}) as Record<string, unknown>

    const generationData: DerivedSpatialDatasetGeneration = {
      source: parseString(generation.source),
      created_by: parseString(generation.created_by),
      processing_signature: parseString(generation.processing_signature),
      group_generation_signature: parseString(
        generation.group_generation_signature
      ),
      tile_generation_signature: parseString(
        generation.tile_generation_signature
      ),
    }
    const geometryData: DerivedSpatialDatasetGeometry = {
      x_measurement_unit: parseString(geometry.x_measurement_unit),
      y_measurement_unit: parseString(geometry.y_measurement_unit),
      z_measurement_unit: parseString(geometry.z_measurement_unit),
    }

    super(
      json,
      {
        dataset_kind: parseString(json.data.dataset_kind) ?? '',
        schema_version: parseNumber(json.data.schema_version),
        data_version: parseNumber(json.data.data_version),
        status: parseString(json.data.status),
        created_at: parseString(json.data.created_at),
        source_inspection_id: parseString(json.data.source_inspection_id),
        source_inspection_run_ids:
          parseArray<string>(json.data.source_inspection_run_ids) ?? [],
        point_schema_version: parseString(json.data.point_schema_version),
        group_schema_version: parseString(json.data.group_schema_version),
        geometry: Object.values(geometryData).some(Boolean)
          ? geometryData
          : undefined,
        amplitude_measurement_unit: parseString(
          json.data.amplitude_measurement_unit
        ),
        depth_measurement_unit: parseString(json.data.depth_measurement_unit),
        files: {
          points_parquet:
            parseString(files.points_parquet) ??
            DERIVED_SPATIAL_DATASET_DEFAULT_FILES.points,
          groups_parquet:
            parseString(files.groups_parquet) ??
            DERIVED_SPATIAL_DATASET_DEFAULT_FILES.groups,
          point_groups_parquet:
            parseString(files.point_groups_parquet) ??
            DERIVED_SPATIAL_DATASET_DEFAULT_FILES.pointGroups,
          tileset: parseString(files.tileset),
          tile_payloads:
            parseString(files.tile_payloads) ??
            parseArray<string>(files.tile_payloads),
        },
        generation: Object.values(generationData).some(Boolean)
          ? generationData
          : undefined,
        error_summary:
          json.data.error_summary === null
            ? null
            : parseString(json.data.error_summary),
      },
      {
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
        component: parseString(json.tags.component) ?? '',
        inspection: parseString(json.tags.inspection) ?? '',
        organization: parseString(json.tags.organization) ?? '',
      },
      {}
    )
  }

  public static CreateFromJSON(
    json: RolodexEntryJson
  ): GeckoDerivedSpatialDataset {
    return new GeckoDerivedSpatialDataset(json)
  }

  public static AreTagsValid(entry: GeckoDerivedSpatialDataset) {
    return (
      entry.tags.organization &&
      entry.tags.site &&
      entry.tags.unit &&
      entry.tags.component &&
      entry.tags.inspection
    )
  }
}
